import {
  Alert,
  Box,
  Card,
  Chip,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useNotify, usePermissions, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { PermissionData } from 'src/pages/admin/entities/permission.entity';
import { hasPermission } from 'src/pages/admin/utils/has-permission';
import { Iconify } from 'src/components/iconify';
import ErrorPage403 from 'src/pages/error/403';
import { XButton } from '../../components/XButton';
import XDateField from '../../components/XDateField';
import { appVersionDataProvider, activateVersion } from './dataProvider';
import { AppVersionInfo, Platform } from './types';
import { isForceUpdate } from './utils';
import PreviewDialog from './PreviewDialog';
import { Resources } from '../resource';

const APP_VERSION_PERMISSION_RESOURCE = 'app-version-updates';

export default function AppVersionList() {
  const navigate = useNavigate();
  const notify = useNotify();
  const translate = useTranslate();
  const { permissions } = usePermissions<PermissionData>();
  const canUpdate = hasPermission(
    permissions?.permissions,
    APP_VERSION_PERMISSION_RESOURCE,
    'update'
  );

  const getPlatformLabel = (p: string) =>
    translate(`module.appVersionManagement.platform.${p}`, { _: p });
  const getFrequencyLabel = (f: string) =>
    translate(`module.appVersionManagement.frequency.${f}`, { _: f });

  const [records, setRecords] = useState<AppVersionInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRecord, setPreviewRecord] = useState<AppVersionInfo | null>(null);

  const activeRecords = records.filter((r) => r.active);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await appVersionDataProvider.getList('', {
        pagination: { page: 1, perPage: 20 },
        sort: { field: 'updatedAt', order: 'DESC' },
        filter: {},
      });
      setRecords(result.data);
    } catch (e: any) {
      notify(e?.message || translate('module.appVersionManagement.notify.loadError'), {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [notify, translate]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleDelete = async (id: string | number) => {
    if (!window.confirm(translate('module.appVersionManagement.confirm.delete'))) return;
    try {
      await appVersionDataProvider.delete('', { id });
      notify(translate('module.appVersionManagement.notify.deleteSuccess'), { type: 'success' });
      fetchList();
    } catch (e: any) {
      notify(e?.message || translate('module.appVersionManagement.notify.deleteError'), {
        type: 'error',
      });
    }
  };

  const handleToggleActive = async (record: AppVersionInfo) => {
    if (record.active) return; // already active, no-op
    try {
      await activateVersion(record.id);
      notify(
        translate('module.appVersionManagement.notify.activateSuccess', {
          versionCode: record.versionCode,
        }),
        { type: 'success' }
      );
      fetchList();
    } catch (e: any) {
      notify(e?.message || translate('module.appVersionManagement.notify.activateError'), {
        type: 'error',
      });
    }
  };

  return (
    <PermissionGuard
      resource={APP_VERSION_PERMISSION_RESOURCE}
      action="read"
      fallback={<ErrorPage403 />}
    >
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('resource.appVersionManagement')}
          links={[
            {
              name: translate('module.appVersionManagement.breadcrumb.list'),
              href: `/${Resources.appVersionManagement}`,
            },
          ]}
          action={
            <PermissionButton resource={APP_VERSION_PERMISSION_RESOURCE} action="create">
              <XButton
                variant="contained"
                label={translate('module.appVersionManagement.newVersion')}
                onClick={() => navigate(`/${Resources.appVersionManagement}/create`)}
                sx={{ minWidth: 120 }}
              />
            </PermissionButton>
          }
        />

        {activeRecords.length > 0 ? (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Alert severity="info" icon={<Iconify icon="eva:info-fill" />} sx={{ mb: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                {translate('module.appVersionManagement.currentlyActive', {
                  versionCode: activeRecords
                    .map((r) => `${r.versionCode} (${getPlatformLabel(r.platform)})`)
                    .join(', '),
                })}
              </Typography>
              <Typography variant="body2">
                {translate('module.appVersionManagement.activeWarning')}
              </Typography>
            </Alert>
          </Stack>
        ) : (
          <Alert
            severity="warning"
            icon={<Iconify icon="eva:alert-triangle-fill" />}
            sx={{ mt: 2, mb: 0 }}
          >
            {translate('module.appVersionManagement.noActiveVersion')}
          </Alert>
        )}

        <Card sx={{ mt: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('module.appVersionManagement.col.version')}</TableCell>
                  <TableCell>{translate('module.appVersionManagement.col.platform')}</TableCell>
                  <TableCell>{translate('module.appVersionManagement.col.frequency')}</TableCell>
                  <TableCell>{translate('module.appVersionManagement.col.force')}</TableCell>
                  <TableCell>{translate('module.appVersionManagement.col.enabled')}</TableCell>
                  <TableCell>{translate('module.appVersionManagement.col.updatedAt')}</TableCell>
                  <TableCell align="right">
                    {translate('module.appVersionManagement.col.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {translate('module.appVersionManagement.noVersions')}
                    </TableCell>
                  </TableRow>
                )}
                {records.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.versionCode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getPlatformLabel(row.platform)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getFrequencyLabel(row.showFrequency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          isForceUpdate(row.versionCode, row.minVersion)
                            ? translate('module.appVersionManagement.yes')
                            : translate('module.appVersionManagement.no')
                        }
                        size="small"
                        color={
                          isForceUpdate(row.versionCode, row.minVersion) ? 'warning' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          row.active
                            ? translate('module.appVersionManagement.active')
                            : translate('module.appVersionManagement.clickToActivate')
                        }
                      >
                        <Switch
                          checked={row.active}
                          onChange={() => handleToggleActive(row)}
                          size="small"
                          disabled={row.active || !canUpdate}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <XDateField value={row.updatedAt} fullDate />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                        <Tooltip title={translate('module.appVersionManagement.preview')}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setPreviewRecord(row);
                              setPreviewOpen(true);
                            }}
                          >
                            <Iconify icon="eva:eye-outline" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={translate('ra.action.edit')}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/${Resources.appVersionManagement}/${row.id}`)}
                          >
                            <Iconify icon="eva:edit-2-outline" />
                          </IconButton>
                        </Tooltip>
                        <PermissionButton
                          resource={APP_VERSION_PERMISSION_RESOURCE}
                          action="delete"
                        >
                          <Tooltip title={translate('ra.action.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(row.id)}
                            >
                              <Iconify icon="eva:trash-2-outline" />
                            </IconButton>
                          </Tooltip>
                        </PermissionButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          versionCode={previewRecord?.versionCode}
          locales={previewRecord?.locales}
          forceUpdate={isForceUpdate(previewRecord?.versionCode, previewRecord?.minVersion)}
          displayType="popup"
        />
      </DashboardContent>
    </PermissionGuard>
  );
}
